import { Pencil, Trash2 } from "lucide-react";
import type { AdminUser } from "@/types/admin";
import { formatAdminDate, formatAdminRelativeDate, getAdminRole } from "@/lib/admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface UserAccountsTableProps {
  users: AdminUser[];
  currentUserId: string | undefined;
  saving: boolean;
  onEdit: (user: AdminUser) => void;
  onDelete: (userId: string) => Promise<void>;
}

export function UserAccountsTable({
  users,
  currentUserId,
  saving,
  onEdit,
  onDelete,
}: UserAccountsTableProps) {
  if (users.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/20 px-6 py-12 text-center">
        <div className="text-sm font-medium text-foreground">No users found</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Create a user or adjust the search term.
        </div>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Last login</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => {
          const isCurrentUser = user.id === currentUserId;
          const role = getAdminRole(user.role);

          return (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Badge variant={role === "admin" ? "default" : "secondary"}>{role}</Badge>
              </TableCell>
              <TableCell>{formatAdminDate(user.createdAt)}</TableCell>
              <TableCell>{formatAdminRelativeDate(user.lastLogin)}</TableCell>
              <TableCell>
                <Badge variant={user.banned ? "destructive" : "outline"}>
                  {user.banned ? "Banned" : "Active"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    onClick={() => onEdit(user)}
                    aria-label={`Edit ${user.email}`}
                  >
                    <Pencil />
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={isCurrentUser || saving}
                        aria-label={`Delete ${user.email}`}
                      >
                        <Trash2 />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Delete User</DialogTitle>
                        <DialogDescription>
                          Delete {user.email}. This removes the account through Better Auth admin.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" type="button">
                            Cancel
                          </Button>
                        </DialogClose>
                        <DialogClose asChild>
                          <Button
                            variant="destructive"
                            type="button"
                            disabled={saving}
                            onClick={() => void onDelete(user.id)}
                          >
                            {saving ? "Deleting..." : "Delete User"}
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
